import React, { PropsWithChildren, useCallback, useContext, useEffect, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import { useForm } from 'react-hook-form'
import dayjs from 'dayjs'
import { Button, ButtonModel, IconRegularCheckmark, IconRegularClock, Spinner } from '@notino/react-styleguide'
import * as SC from './Step2EmployeeAndDateSelectionGroupStyles'
import ReservationBookingModalLayout from '../ReservationBookingModalLayout/ReservationBookingModalLayout'
import HeaderContent from '../HeaderContent/HeaderContent'
import ErrorScreen from '../../../atoms/ErrorScreen/ErrorScreen'
import { CART_RESERVATION_SUBMIT_ERROR_CODE, DEFAULT_DATE_FORMAT, FORM, MSG_TYPE } from '../../../utils/enums'
import useMessages from '../../../hooks/useMessages'
import {
	CalendarEventGroupItems,
	GetPaginatedCalendarEventsGroupReservationAvailableParams,
	GetPaginatedCalendarEventsGroupReservationAvailableResponse,
	PostCartGroupReservationBody,
	ReservationBookingEmployeeAndDateGroupSelectionForm,
	ReservationBookingEmployeeAndDateGroupSelectionSubmittedForm,
	ReservationBookingSelectedFormValues,
	SalonServiceResponse,
	ServiceCategoryParameterValueData
} from '../../../types/types'
import {
	ANYONE_AVAILABLE_OPTION_VALUE,
	EmployeeOption,
	getCartGroupReservationRequestBody,
	getCartReservationSubmitErrorCode,
	getAnyoneAvailableOption,
	getEmployeeOptions,
	getSelectedTimeSlotGroupValue,
	scrollElementIntoView
} from '../reservationBookingModalHelpers'
import HookFormField from '../../../formFields/HookFormField'
import RadioDateSlotsFormField from '../../../formFields/RadioDateSlotsFormField/RadioDateSlotsFormField'
import collapseMotion from '../Step2EmployeeAndDateSelection/collapseMotion'
import ResourceCard from '../../ResourceCard/ResourceCard'
import UserIcon from '../../../assets/icons/UserIcon'
import RenderDuration from '../../RenderDuration/RenderDuration'
import RenderPrice from '../../RenderPrice/RenderPrice'
import CheckboxGroupCustomFormField from '../../../formFields/CheckboxGroupCustomFormField/CheckboxGroupCustomFormField'
import { CheckboxGroupCustomFormFieldProps } from '../../../formFields/CheckboxGroupCustomFormField/types'
import { ERROR_MESSAGES, formatReservationDateRange, getDateTime, showErrorNotifications, showNotifications } from '../../../utils/helper'
import ReservationBookingDialog from '../ReservationBookingDialog/ReservationBookingDialog'
import * as DialogSC from '../ReservationBookingDialog/ReservationBookingDialogStyles'
import Empty from '../../../atoms/Empty/Empty'
import VisuallyHidden from '../../../atoms/VisuallyHidden/VisuallyHidden'
import { AppContext } from '../../../utils/appProvider'

type Props = PropsWithChildren<{
	salonID: string
	selectedFormValues: ReservationBookingSelectedFormValues
	selectedIndustryID: string | undefined
	selectedServiceData: SalonServiceResponse['service']
	selectedServiceCategoryParameterValueData: ServiceCategoryParameterValueData | undefined
	headerImage: string | undefined
	isAddingAnotherServiceFromSummary: boolean
	maxCountCartReservations: number
	onViewReservationsSummary: () => void
	editingCartReservationItemID: string | null
	onClose: () => void
	onBackButtonClick?: () => void
	handleSubmitEmployeeAndDateGroupSelectionForm: (
		values: ReservationBookingEmployeeAndDateGroupSelectionSubmittedForm,
		options?: { keepCartEditState?: boolean }
	) => void
	onPersistGroupCartReservation: (
		requestBody: PostCartGroupReservationBody,
		submittedData: ReservationBookingEmployeeAndDateGroupSelectionSubmittedForm,
		cartReservationItemID: string | null
	) => Promise<void>
	availableGroupSlots: GetPaginatedCalendarEventsGroupReservationAvailableResponse | null
	isFetching: boolean
	setIsFetching: (isFetching: boolean) => void
	loadingError: boolean
	fetchGroupSlots: (
		salonID: string,
		query: GetPaginatedCalendarEventsGroupReservationAvailableParams,
		append?: boolean
	) => Promise<GetPaginatedCalendarEventsGroupReservationAvailableResponse | null>
}>

const EMPLOYEE_COLLAPSE_KEY = 'EMPLOYEE_COLLAPSE_KEY'
const EMPLOYEE_COLLAPSE_TRIGGER_CLASS = 'employee-collapse-trigger'

const GROUP_SLOTS_ANYONE_CHECK_LIMIT = 10

const GROUP_DATE_SLOTS_SKELETON_COUNT = 3

const GroupDateSlotsSkeleton = () => (
	<>
		{Array.from({ length: GROUP_DATE_SLOTS_SKELETON_COUNT }).map((_item, index) => (
			<SC.SlotWrapper key={index}>
				<SC.DateHeaderSkeleton />
				<SC.SlotCardSkeleton>
					<SC.SlotCardSkeletonContent>
						<SC.SlotTimeSkeleton />
						<SC.SlotFooterSkeleton>
							<SC.SlotMetaSkeleton $width={'42%'} />
							<SC.SlotMetaSkeleton $width={'22%'} />
						</SC.SlotFooterSkeleton>
					</SC.SlotCardSkeletonContent>
					<SC.SlotRadioSkeleton />
				</SC.SlotCardSkeleton>
			</SC.SlotWrapper>
		))}
	</>
)

const Step2EmployeeAndDateSelectionGroup = (props: Props) => {
	const { formatDate, formatMessage } = useIntl()
	const { messages } = useMessages()
	const employeeCollapseRef = useRef<HTMLDivElement | null>(null)
	const observerRef = useRef<HTMLDivElement | null>(null)
	const { isAuthorized, apiBrowser } = useContext(AppContext)

	const {
		onClose,
		onBackButtonClick,
		selectedServiceData,
		selectedServiceCategoryParameterValueData,
		headerImage,
		salonID,
		selectedFormValues,
		handleSubmitEmployeeAndDateGroupSelectionForm,
		isFetching,
		fetchGroupSlots,
		loadingError,
		availableGroupSlots,
		setIsFetching,
		isAddingAnotherServiceFromSummary,
		maxCountCartReservations,
		onViewReservationsSummary,
		editingCartReservationItemID,
		onPersistGroupCartReservation
	} = props
	const [employeeCollapseActiveKeys, setEmployeeCollapseActiveKeys] = useState<React.Key[]>([])
	const [isSubmittingCartReservation, setIsSubmittingCartReservation] = useState(false)
	const [isAddReservationErrorModalOpen, setIsAddReservationErrorModalOpen] = useState(false)
	const [isFullCartModalOpen, setIsFullCartModalOpen] = useState(false)
	const [isServiceAlreadyBookedModalOpen, setIsServiceAlreadyBookedModalOpen] = useState(false)
	const [anyoneHasGroupSlots, setAnyoneHasGroupSlots] = useState<boolean | null>(null)
	const [isCheckingAnyoneGroupSlots, setIsCheckingAnyoneGroupSlots] = useState(false)
	const retryAddReservationRef = useRef<(() => Promise<void>) | null>(null)
	const hasAutoSwitchedToAnyoneRef = useRef(false)
	const preselectedEmployeeIDRef = useRef(selectedFormValues.EMPLOYEE_AND_DATE_GROUP_SELECTION.employeeID)
	const singleEmployee = selectedServiceData.employees.length === 1

	const { control, handleSubmit, watch, setValue } = useForm<ReservationBookingEmployeeAndDateGroupSelectionForm>({
		defaultValues: {
			employeeID: [selectedFormValues.EMPLOYEE_AND_DATE_GROUP_SELECTION.employeeID || ANYONE_AVAILABLE_OPTION_VALUE],
			date: selectedFormValues.EMPLOYEE_AND_DATE_GROUP_SELECTION.date,
			eventID: selectedFormValues.EMPLOYEE_AND_DATE_GROUP_SELECTION.eventID
		}
	})
	const formValues = watch()
	const { date, employeeID: employeeIDValue, eventID } = formValues
	const [employeeID] = employeeIDValue

	// Button from Notino style-guide library doesn't recognize "form" as a prop, however, when passed to the component, it works fine
	const unknownButtonProps = {
		form: FORM.RESERVATION_BOOKING_MODAL_EMPLOYEE_AND_DATE_GROUP_SELECTION
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
	} as any

	const parameterName =
		selectedServiceCategoryParameterValueData?.value && selectedServiceData.serviceCategoryParameter?.name
			? `${selectedServiceData.serviceCategoryParameter.name}: ${selectedServiceCategoryParameterValueData.value}`
			: selectedServiceCategoryParameterValueData?.value
	const foundSlot = getSelectedTimeSlotGroupValue(availableGroupSlots?.availableReservations || [], eventID)

	const isLoadingSlots = availableGroupSlots === null
	const isLoadingMoreSlots = isFetching && !isLoadingSlots
	const isFullyBooked = !isLoadingSlots && (!availableGroupSlots.availableReservations || availableGroupSlots.availableReservations.length === 0)
	// Event already in the cart is a disabled option; BE rejects PUT with it (409).
	const isFoundSlotInCart = Boolean(foundSlot?.item.calendarEvent.hasUserCartReservationItem)
	const disabledConfirmButton = !date || !employeeID || !eventID || isFoundSlotInCart || isLoadingSlots || isSubmittingCartReservation

	const checkAnyoneGroupSlots = useCallback(async () => {
		if (singleEmployee || employeeID === ANYONE_AVAILABLE_OPTION_VALUE) {
			setAnyoneHasGroupSlots(null)
			return
		}

		setIsCheckingAnyoneGroupSlots(true)

		try {
			const result = await apiBrowser.b2c.getPaginatedCalendarEventsGroupReservationAvailable(salonID, {
				serviceID: selectedServiceData.id,
				dateFrom: dayjs().format(DEFAULT_DATE_FORMAT),
				page: 1,
				limit: GROUP_SLOTS_ANYONE_CHECK_LIMIT
			})

			setAnyoneHasGroupSlots((result?.availableReservations?.length ?? 0) > 0)
		} catch {
			setAnyoneHasGroupSlots(false)
		} finally {
			setIsCheckingAnyoneGroupSlots(false)
		}
	}, [apiBrowser.b2c, employeeID, salonID, selectedServiceData.id, singleEmployee])

	useEffect(() => {
		if (isLoadingSlots || !isFullyBooked || singleEmployee || employeeID === ANYONE_AVAILABLE_OPTION_VALUE) {
			return
		}

		// Book again / init-preselected employee with no future group events → switch to Anyone available once.
		// Manual employee selection keeps the empty state + Change employee CTA.
		const isPreselectedEmployeeWithoutSlots = Boolean(
			preselectedEmployeeIDRef.current && employeeID === preselectedEmployeeIDRef.current && !hasAutoSwitchedToAnyoneRef.current
		)

		if (isPreselectedEmployeeWithoutSlots) {
			hasAutoSwitchedToAnyoneRef.current = true
			setAnyoneHasGroupSlots(null)
			setValue('eventID', null)
			setValue('employeeID', [ANYONE_AVAILABLE_OPTION_VALUE])
			fetchGroupSlots(salonID, {
				serviceID: selectedServiceData.id,
				dateFrom: dayjs().format(DEFAULT_DATE_FORMAT),
				page: 1
			}).catch(() => undefined)
			return
		}

		checkAnyoneGroupSlots().catch(() => undefined)
	}, [checkAnyoneGroupSlots, employeeID, fetchGroupSlots, isFullyBooked, isLoadingSlots, salonID, selectedServiceData.id, setValue, singleEmployee])

	const anyoneAvailableOption = getAnyoneAvailableOption(selectedServiceData, selectedServiceCategoryParameterValueData)
	const employeeOptions = getEmployeeOptions(
		anyoneAvailableOption,
		selectedServiceData,
		selectedServiceCategoryParameterValueData,
		singleEmployee ? [selectedServiceData.employees[0].id] : undefined
	)

	const selectedEmployeeData =
		employeeOptions.find((employeeOption) => {
			return employeeID === employeeOption.value
		}) || anyoneAvailableOption

	const reloadGroupSlots = () => {
		setValue('eventID', null)
		fetchGroupSlots(salonID, {
			serviceID: selectedServiceData.id,
			employeeID: employeeID !== ANYONE_AVAILABLE_OPTION_VALUE ? employeeID : undefined,
			dateFrom: dayjs().format(DEFAULT_DATE_FORMAT),
			page: 1
		})
	}

	const submitCartReservation = async (
		requestBody: PostCartGroupReservationBody,
		submittedData: ReservationBookingEmployeeAndDateGroupSelectionSubmittedForm
	) => {
		setIsSubmittingCartReservation(true)

		const submitRequest = async () => {
			await onPersistGroupCartReservation(requestBody, submittedData, editingCartReservationItemID)
		}

		try {
			await submitRequest()
		} catch (error) {
			const errorCode = getCartReservationSubmitErrorCode(error)

			if (errorCode === CART_RESERVATION_SUBMIT_ERROR_CODE.CART_IS_FULL) {
				setIsFullCartModalOpen(true)
				return
			}

			if (errorCode === CART_RESERVATION_SUBMIT_ERROR_CODE.ALREADY_BOOKED) {
				setIsServiceAlreadyBookedModalOpen(true)
				return
			}

			if (isAddingAnotherServiceFromSummary || editingCartReservationItemID) {
				retryAddReservationRef.current = submitRequest
				setIsAddReservationErrorModalOpen(true)
				return
			}

			showErrorNotifications(error, { force: true })
		} finally {
			setIsSubmittingCartReservation(false)
		}
	}

	const handleFullCartModalClose = () => {
		setIsFullCartModalOpen(false)
	}

	const handleFullCartModalViewReservations = () => {
		setIsFullCartModalOpen(false)
		onViewReservationsSummary()
	}

	const handleServiceAlreadyBookedModalConfirm = () => {
		setIsServiceAlreadyBookedModalOpen(false)
		reloadGroupSlots()
	}

	const handleAddReservationErrorModalClose = () => {
		if (isSubmittingCartReservation) {
			return
		}

		setIsAddReservationErrorModalOpen(false)
		retryAddReservationRef.current = null
	}

	const handleAddReservationErrorModalRetry = async () => {
		const retry = retryAddReservationRef.current

		if (!retry) {
			return
		}

		setIsSubmittingCartReservation(true)

		try {
			await retry()
			setIsAddReservationErrorModalOpen(false)
			retryAddReservationRef.current = null
		} catch {
			// Keep the error modal open for another retry attempt.
		} finally {
			setIsSubmittingCartReservation(false)
		}
	}

	const handleSubmitForm = async (values: ReservationBookingEmployeeAndDateGroupSelectionForm) => {
		if (!foundSlot?.item.from && !foundSlot?.item.to) {
			// The application shouldn't be able to reach this state
			// But to avoid having just a return with nothing happening, an error message is displayed here as a fallback
			showNotifications([{ type: MSG_TYPE.ERROR, message: ERROR_MESSAGES().GENERAL_ERROR }])
			return
		}

		const submitDataEmployeeID = values.employeeID[0] !== ANYONE_AVAILABLE_OPTION_VALUE ? values.employeeID[0] : null

		const submittedData: ReservationBookingEmployeeAndDateGroupSelectionSubmittedForm = {
			employeeID: submitDataEmployeeID,
			date: foundSlot.date,
			eventID,
			timeSlot: {
				employeeID: foundSlot.item.calendarEvent.employee.id,
				timeTo: foundSlot.item.to,
				timeFrom: foundSlot.item.from
			}
		}

		if (!eventID) {
			showNotifications([{ type: MSG_TYPE.ERROR, message: ERROR_MESSAGES().GENERAL_ERROR }])
			return
		}

		const requestBody = getCartGroupReservationRequestBody(eventID)

		if (!isAuthorized) {
			if (editingCartReservationItemID || isAddingAnotherServiceFromSummary) {
				await submitCartReservation(requestBody, submittedData)
				return
			}

			handleSubmitEmployeeAndDateGroupSelectionForm(submittedData)
			return
		}

		await submitCartReservation(requestBody, submittedData)
	}

	const eventOptions = (reservationDate: string, calendarEventItems: CalendarEventGroupItems) => {
		if (!calendarEventItems) return []

		return calendarEventItems.map((item) => {
			const event = item.calendarEvent
			const spotsLeft = event.capacity.maxReservationsCount - event.capacity.occupiedReservationsCount

			return {
				id: event.id,
				label: `${item.from} - ${item.to}`,
				accessibleLabel: formatReservationDateRange(
					formatDate,
					getDateTime(reservationDate, item.from).toDate(),
					getDateTime(reservationDate, item.to).toDate()
				),
				spotsLeft,
				disabled: event.hasUserCartReservationItem,
				capacity: event.capacity.maxReservationsCount,
				alreadyBooked: event.hasUserReservation
			}
		})
	}

	const renderGroupDateSlots = () => {
		if (isLoadingSlots || isCheckingAnyoneGroupSlots) {
			return <GroupDateSlotsSkeleton />
		}

		if (isFullyBooked) {
			const isSpecificEmployee = employeeID && employeeID !== ANYONE_AVAILABLE_OPTION_VALUE
			const showChangeEmployeeCTA = !singleEmployee && !!isSpecificEmployee && anyoneHasGroupSlots === true

			return (
				<Empty
					minHeight={'200px'}
					role={'status'}
					title={messages['No available time slots']}
					label={
						showChangeEmployeeCTA
							? messages['There are currently no available time slots for this employee. Please select another employee.']
							: messages['There are currently no available time slots']
					}
					button={
						showChangeEmployeeCTA
							? {
									label: messages['Change employee'],
									onClick: () => {
										if (employeeCollapseRef.current) {
											employeeCollapseRef.current
												?.querySelector<HTMLElement>(`.${EMPLOYEE_COLLAPSE_TRIGGER_CLASS}`)
												?.focus({ preventScroll: false })
											scrollElementIntoView(employeeCollapseRef.current)
										}

										setEmployeeCollapseActiveKeys([EMPLOYEE_COLLAPSE_KEY])
									}
								}
							: undefined
					}
				/>
			)
		}

		return (
			<>
				<HookFormField
					control={control}
					name={'eventID'}
					component={RadioDateSlotsFormField}
					label={messages['Select time']}
					labelScreenReaderOnly
					optionGroups={availableGroupSlots.availableReservations.map((availableReservation) => ({
						id: availableReservation.date,
						label: formatDate(dayjs(availableReservation.date).toDate(), {
							weekday: 'long',
							day: '2-digit',
							month: '2-digit'
						}),
						options: eventOptions(availableReservation.date, availableReservation.items)
					}))}
				/>
				{isLoadingMoreSlots && <GroupDateSlotsSkeleton />}
			</>
		)
	}

	// Infinity scroll — len volá parent callback
	useEffect(() => {
		let observer: IntersectionObserver | null = null
		if (observerRef.current) {
			observer = new IntersectionObserver(
				(entries) => {
					const first = entries[0]
					if (
						first.isIntersecting &&
						!isFetching &&
						availableGroupSlots &&
						availableGroupSlots.pagination.page < availableGroupSlots.pagination.totalPages
					) {
						setIsFetching(true)
						const nextPage = availableGroupSlots.pagination.page + 1
						fetchGroupSlots(
							salonID,
							{
								serviceID: selectedServiceData.id,
								employeeID: employeeID !== ANYONE_AVAILABLE_OPTION_VALUE ? employeeID : undefined,
								dateFrom: dayjs().format(DEFAULT_DATE_FORMAT),
								page: nextPage
							},
							true
						)
					}
				},
				{ threshold: 1.0 }
			)
			observer.observe(observerRef.current)
		}
		return () => observer?.disconnect()
	}, [availableGroupSlots, date, employeeID, fetchGroupSlots, isFetching, salonID, selectedServiceData.id, setIsFetching])

	const employeeOptionRender = (option: EmployeeOption, isChecked: boolean, isDisabled: boolean, isTimeSlotEmployee: boolean) => {
		return (
			<SC.EmployeeOptionWrapper $isDisabled={isDisabled} $isTimeSlotEmployee={isTimeSlotEmployee}>
				<ResourceCard
					extraContent={isChecked ? <IconRegularCheckmark /> : null}
					title={option.label}
					avatarUrl={option.extra?.avatar}
					customIcon={<UserIcon />}
					infoContent={
						<SC.PriceAndDurationWrapper>
							<SC.DurationWrapper>
								<IconRegularClock color={'icon.secondary'} width={'16px'} height={'16px'} aria-hidden />
								<RenderDuration from={option.extra?.priceAndDurationData?.durationFrom} to={option.extra?.priceAndDurationData?.durationTo} />
							</SC.DurationWrapper>
							<RenderPrice
								from={option.extra?.priceAndDurationData?.priceFrom}
								to={option.extra?.priceAndDurationData?.priceTo}
								fromSecondary={option.extra?.priceAndDurationData?.priceFromSecondary}
								toSecondary={option.extra?.priceAndDurationData?.priceToSecondary}
							/>
						</SC.PriceAndDurationWrapper>
					}
				/>
			</SC.EmployeeOptionWrapper>
		)
	}
	const afterEmployeeChange: CheckboxGroupCustomFormFieldProps<string[], EmployeeOption>['afterChange'] = async (value) => {
		// Move focus to the collapse header before radios unmount so it never falls to the page CTA.
		employeeCollapseRef.current?.querySelector<HTMLElement>(`.${EMPLOYEE_COLLAPSE_TRIGGER_CLASS}`)?.focus({ preventScroll: true })
		setEmployeeCollapseActiveKeys([])
		setAnyoneHasGroupSlots(null)
		// reset selected time slot
		setValue('eventID', null)
		const newEmployeeID = value[0]
		await fetchGroupSlots(salonID, {
			serviceID: selectedServiceData.id,
			employeeID: newEmployeeID !== ANYONE_AVAILABLE_OPTION_VALUE ? newEmployeeID : undefined,
			dateFrom: dayjs().format(DEFAULT_DATE_FORMAT),
			page: 1
		})
	}

	return (
		<>
			<ReservationBookingModalLayout
				title={messages.Date}
				headerImage={headerImage}
				onBackButtonClick={onBackButtonClick}
				onClose={onClose}
				headerContent={<HeaderContent title={selectedServiceData.category.child.child?.name ?? ''} description={parameterName} />}
				footerContent={
					<SC.FooterWrapper>
						<SC.DateTimeInfo>
							{foundSlot?.date && foundSlot?.item.from && foundSlot?.item.to ? (
								<>
									<SC.SelectedDate>
										{formatDate(dayjs(foundSlot.date).toDate(), { weekday: 'long', day: '2-digit', month: '2-digit' })}
									</SC.SelectedDate>
									<SC.SelectedTimeSlot>{`${foundSlot.item.from} - ${foundSlot.item.to}`}</SC.SelectedTimeSlot>
								</>
							) : null}
						</SC.DateTimeInfo>
						<SC.ConfirmButton
							{...unknownButtonProps}
							type={'submit'}
							buttonStyle={ButtonModel.Styles.primary}
							disabled={disabledConfirmButton}
							aria-busy={isSubmittingCartReservation}
						>
							{isSubmittingCartReservation ? (
								<>
									<Spinner size={16} color={'icon.disabled'} aria-hidden />
									<VisuallyHidden>{messages.Confirm}</VisuallyHidden>
								</>
							) : (
								messages.Confirm
							)}
						</SC.ConfirmButton>
					</SC.FooterWrapper>
				}
			>
				{(() => {
					if (loadingError) {
						return (
							<ErrorScreen
								role={'alert'}
								button={{
									onClick: () =>
										fetchGroupSlots(salonID, {
											serviceID: selectedServiceData.id,
											employeeID: employeeID !== ANYONE_AVAILABLE_OPTION_VALUE ? employeeID : undefined,
											dateFrom: dayjs().format(DEFAULT_DATE_FORMAT),
											page: 1
										})
								}}
							/>
						)
					}

					return (
						<SC.Form id={FORM.RESERVATION_BOOKING_MODAL_EMPLOYEE_AND_DATE_GROUP_SELECTION} onSubmit={handleSubmit(handleSubmitForm)}>
							<SC.EmployeeCollapseWrapper
								ref={employeeCollapseRef}
								activeKey={employeeCollapseActiveKeys}
								openMotion={collapseMotion}
								$collapsible={!singleEmployee}
								onChange={(newActiveKeys) => {
									setEmployeeCollapseActiveKeys(Array.isArray(newActiveKeys) ? newActiveKeys : [newActiveKeys])
								}}
								collapsible={singleEmployee ? 'disabled' : 'header'}
								items={[
									{
										key: EMPLOYEE_COLLAPSE_KEY,
										classNames: {
											title: EMPLOYEE_COLLAPSE_TRIGGER_CLASS
										},
										label: (
											<SC.CardWrapper $collapsible={!singleEmployee}>
												{!singleEmployee ? <VisuallyHidden>{messages['Select the specialist']}</VisuallyHidden> : null}
												<ResourceCard
													customIcon={<UserIcon />}
													title={selectedEmployeeData.label}
													avatarUrl={selectedEmployeeData.extra?.avatar}
													infoContent={
														<SC.PriceAndDurationWrapper>
															<SC.DurationWrapper>
																<IconRegularClock color={'icon.secondary'} width={'16px'} height={'16px'} aria-hidden />
																<RenderDuration
																	from={selectedEmployeeData.extra?.priceAndDurationData?.durationFrom}
																	to={selectedEmployeeData.extra?.priceAndDurationData?.durationTo}
																/>
															</SC.DurationWrapper>
															<RenderPrice
																from={selectedEmployeeData.extra?.priceAndDurationData?.priceFrom}
																to={selectedEmployeeData.extra?.priceAndDurationData?.priceTo}
																fromSecondary={selectedEmployeeData.extra?.priceAndDurationData?.priceFromSecondary}
																toSecondary={selectedEmployeeData.extra?.priceAndDurationData?.priceToSecondary}
															/>
														</SC.PriceAndDurationWrapper>
													}
													extraContent={
														singleEmployee ? null : (
															<SC.ChevronIcon
																$isActive={!!employeeCollapseActiveKeys.length}
																color={'icon.primary'}
																width={'1.5rem'}
																height={'1.5rem'}
																aria-hidden
															/>
														)
													}
												/>
											</SC.CardWrapper>
										),
										children: (
											<HookFormField
												control={control}
												name={'employeeID'}
												readOnly={isFetching}
												component={CheckboxGroupCustomFormField<string[], EmployeeOption>}
												options={employeeOptions}
												optionRender={(option: EmployeeOption, isChecked: boolean, isDisabled: boolean) =>
													employeeOptionRender(option, isChecked, isDisabled, false)
												}
												afterChange={afterEmployeeChange}
												alignment={'vertical'}
												singleSelect
												gap={'0'}
												allowUnselect={false}
												label={messages['Select the specialist']}
												labelScreenReaderOnly
											/>
										)
									}
								]}
							/>
							<SC.SlotsRegion aria-busy={isLoadingSlots || isCheckingAnyoneGroupSlots || isLoadingMoreSlots || undefined}>
								{(isLoadingSlots || isCheckingAnyoneGroupSlots) && (
									<VisuallyHidden role={'status'}>{formatMessage({ id: 'Loading', defaultMessage: 'Loading' })}</VisuallyHidden>
								)}
								{renderGroupDateSlots()}
							</SC.SlotsRegion>
							{/* Sentinel for infinity scroll */}
							<div ref={observerRef} style={{ height: '1px' }} />
						</SC.Form>
					)
				})()}
			</ReservationBookingModalLayout>
			<ReservationBookingDialog
				isOpen={isAddReservationErrorModalOpen}
				onClose={handleAddReservationErrorModalClose}
				showHeaderCloseButton
				isHeaderCloseButtonDisabled={isSubmittingCartReservation}
				title={formatMessage({ id: 'Failed to add the reservation', defaultMessage: 'Failed to add the reservation' })}
				description={formatMessage({
					id: 'We could not add the service to your reservation. Please try again.',
					defaultMessage: 'We could not add the service to your reservation. Please try again.'
				})}
				footer={
					<DialogSC.Footer>
						<DialogSC.FooterButton>
							<Button
								type={'button'}
								buttonStyle={ButtonModel.Styles.secondary}
								fullWidth
								disabled={isSubmittingCartReservation}
								onClick={handleAddReservationErrorModalClose}
							>
								{formatMessage({ id: 'Close booking modal', defaultMessage: 'Close' })}
							</Button>
						</DialogSC.FooterButton>
						<DialogSC.FooterButton>
							<Button
								type={'button'}
								buttonStyle={ButtonModel.Styles.primary}
								fullWidth
								disabled={isSubmittingCartReservation}
								aria-busy={isSubmittingCartReservation}
								onClick={handleAddReservationErrorModalRetry}
							>
								{isSubmittingCartReservation ? (
									<>
										<Spinner size={16} color={'icon.disabled'} aria-hidden />
										<VisuallyHidden>{formatMessage({ id: 'Try again', defaultMessage: 'Try again' })}</VisuallyHidden>
									</>
								) : (
									formatMessage({ id: 'Try again', defaultMessage: 'Try again' })
								)}
							</Button>
						</DialogSC.FooterButton>
					</DialogSC.Footer>
				}
			/>
			<ReservationBookingDialog
				isOpen={isFullCartModalOpen}
				onClose={handleFullCartModalClose}
				title={formatMessage({ id: "Couldn't add service", defaultMessage: "Couldn't add service" })}
				description={formatMessage(
					{
						id: 'You already have {maxCountReservations} services in your reservation. Try deleting a service first, then add this one.',
						defaultMessage: 'You already have {maxCountReservations} services in your reservation. Try deleting a service first, then add this one.'
					},
					{ maxCountReservations: maxCountCartReservations }
				)}
				footer={
					<DialogSC.Footer>
						<DialogSC.FooterButton>
							<Button type={'button'} buttonStyle={ButtonModel.Styles.secondary} fullWidth onClick={handleFullCartModalClose}>
								{formatMessage({ id: 'Close booking modal', defaultMessage: 'Close' })}
							</Button>
						</DialogSC.FooterButton>
						<DialogSC.FooterButton>
							<Button type={'button'} buttonStyle={ButtonModel.Styles.primary} fullWidth onClick={handleFullCartModalViewReservations}>
								{formatMessage({ id: 'View reservations', defaultMessage: 'View reservations' })}
							</Button>
						</DialogSC.FooterButton>
					</DialogSC.Footer>
				}
			/>
			<ReservationBookingDialog
				isOpen={isServiceAlreadyBookedModalOpen}
				onClose={handleServiceAlreadyBookedModalConfirm}
				title={formatMessage({ id: 'Service has been booked!', defaultMessage: 'Service has been booked!' })}
				description={formatMessage({
					id: 'Some of your services have been booked. Please select another time slot, change the employee or choose another service.',
					defaultMessage: 'Some of your services have been booked. Please select another time slot, change the employee or choose another service.'
				})}
				footer={
					<Button type={'button'} buttonStyle={ButtonModel.Styles.primary} fullWidth onClick={handleServiceAlreadyBookedModalConfirm}>
						{formatMessage({ id: 'OK', defaultMessage: 'OK' })}
					</Button>
				}
			/>
		</>
	)
}

export default Step2EmployeeAndDateSelectionGroup
